import { useCallback } from 'react';
import { trpc } from '@client-trpc';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useWindow } from '@client/window/window.hook';
import type { LayoutSnapshot } from '@server/trading-layouts/schemas';
import type { SaveTradingLayoutFormState } from './save-trading-layout-form';

type SavedLayoutRef = {
  layoutId: string;
  layoutName: string;
};

export const useCreateLayout = () => {
  const { notify } = useNotifications();
  const { closeWindow } = useWindow();
  const utils = trpc.useUtils();
  const { mutateAsync: createLayout } =
    trpc.tradingLayouts.createLayout.useMutation();
  const { mutateAsync: updateLayout } =
    trpc.tradingLayouts.updateLayout.useMutation();

  const onCreateSubmit = useCallback(
    (snapshot: LayoutSnapshot) =>
      async (formState: SaveTradingLayoutFormState): Promise<
        SavedLayoutRef | null
      > => {
        try {
          const layoutId = await createLayout({
            name: formState.name,
            notes: formState.notes?.trim() || undefined,
            snapshot
          });

          await utils.tradingLayouts.layouts.invalidate();

          notify({
            type: 'success',
            message: 'Layout saved successfully'
          });

          closeWindow('SaveTradingLayoutForm');
          // Return created id so caller can link current form to this layout immediately.
          return {
            layoutId,
            layoutName: formState.name
          };
        } catch (error) {
          notify({
            type: 'error',
            message:
              error instanceof Error ? error.message : 'Failed to save layout'
          });
          return null;
        }
      },
    [closeWindow, createLayout, notify, utils]
  );

  const onUpdateSubmit = useCallback(
    (layoutId: string, snapshot: LayoutSnapshot) =>
      async (formState: SaveTradingLayoutFormState): Promise<
        SavedLayoutRef | null
      > => {
        try {
          await updateLayout({
            layoutId,
            name: formState.name,
            notes: formState.notes?.trim() || undefined,
            snapshot
          });

          await utils.tradingLayouts.layouts.invalidate();

          notify({
            type: 'success',
            message: 'Layout updated successfully'
          });

          closeWindow('SaveTradingLayoutForm');
          // Return the same id with possibly new name after update.
          return {
            layoutId,
            layoutName: formState.name
          };
        } catch (error) {
          notify({
            type: 'error',
            message:
              error instanceof Error ? error.message : 'Failed to update layout'
          });
          return null;
        }
      },
    [closeWindow, notify, updateLayout, utils]
  );

  return {
    onCreateSubmit,
    onUpdateSubmit
  };
};
